import * as React from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { LuArrowRight, LuChevronDown } from 'react-icons/lu';

import { cn } from '@mds/shared/lib/utils';
import { useReveal } from '@mds/shared/hooks/use-reveal';
import { ImageGallery } from '@mds/shared/ui/image/image-gallery';

import AnswerPanel from '@/components/section/answer-panel';
import CardRail from '@/components/card/card-rail';
import FeatureSplit from '@/components/section/feature-split';
import SpaceCard from '@/components/card/space-card';
import RuledHeader from '@/components/heading/ruled-header';
import RuledList from '@/components/section/ruled-list';
import StatementSwipe from '@/components/section/statement-swipe';
import { ErrorBlock, LoadingBlock } from '@/components/states';

import { ABOUT } from '@/data';
import { galleryQueries } from '@/features/gallery/gallery.queries';
import { spaceQueries } from '@/features/spaces/spaces.queries';
const band = 'mx-auto max-w-page px-gutter pt-section';

/**
 * The photo strip under the opening statement: every public image in the
 * "About-images" folder of the admin file library, in name order. Staff change
 * the strip from the Files page — upload into the folder as Public, or rename
 * to reorder — with no deploy.
 *
 * Not drawn while the list loads, if it fails, or when the folder holds no
 * public images: the strip illustrates the statement above it, and the page
 * reads whole without it.
 *
 * TODO: no alt text until each photo is matched to the room it shows; the
 * gallery names each tile by its position meanwhile.
 */
function PhotoStrip() {
  const gallery = useQuery(galleryQueries.detail('about'));
  const images = React.useMemo(
    () => (gallery.data ?? []).map((file) => file.contentUrl),
    [gallery.data],
  );

  if (images.length === 0) return null;

  return (
    <section className="mx-auto max-w-page px-gutter pt-block">
      {/* No heading: the pictures follow the opening statement as its
          illustration, not as a section of their own. The strip still
          carries its name for a screen reader through `label`. */}
      <ImageGallery label="Inside MDS" images={images} layout="marquee" />
    </section>
  );
}

/**
 * The statement band: a display headline with its last words marked in mint
 * and a lead under it on the left, and the two points that answer it on the
 * right.
 *
 * Written here rather than as a component: it has one call site. If a second
 * page wants a marked headline, that is the moment it becomes `Statement`.
 *
 * The left column reveals with the same rise and stagger `RuledList` uses on
 * the right, so the two halves arrive as one gesture.
 */
function ProblemSection() {
  const { problem } = ABOUT;
  const { ref, state } = useReveal<HTMLDivElement>();

  // The same three states `RuledList` reads: drawn plainly, held hidden until
  // scrolled to, then rising in with the nth element waiting n stagger steps.
  const rise = cn(state === 'armed' && 'opacity-0', state === 'revealed' && 'animate-reveal-up');
  const after = (step: number) =>
    state === 'revealed'
      ? { animationDelay: `calc(var(--duration-stagger) * ${step})` }
      : undefined;

  return (
    <section className={band}>
      <RuledHeader title="Why MDS exists" />

      <div className="grid items-start gap-block lg:grid-cols-2">
        <div ref={ref}>
          {/* `h3`: "Brand purpose" above is the section's heading, and this
              line is the statement under it. */}
          <h3
            style={after(0)}
            // Under the chapter heading's size at every width, so the
            // statement never outranks the section's own title. Green, so the
            // black chapter title over it and the statement read as two voices.
            className={cn('text-2xl text-balance text-brand-text lg:text-3xl', rise)}
          >
            {problem.title} {problem.highlight}
          </h3>
          <p style={after(1)} className={cn('mt-6 max-w-prose text-body-lg text-ink', rise)}>
            {problem.lead}
          </p>
        </div>

        {/* `text-ink-muted` on the list rather than a prop: with no titles the
            sentences are `text-current`, so they take the colour set here. */}
        <RuledList
          rules="between"
          spacing="relaxed"
          accent="brand"
          className="text-ink-muted lg:pt-3"
          items={problem.points.map((point, i) => ({
            marker: String(i + 1).padStart(2, '0'),
            description: point,
          }))}
        />
      </div>
    </section>
  );
}

/**
 * The overview: four questions as a selectable `RuledList` on the left, and
 * the `AnswerPanel` beside it answering whichever one is chosen.
 *
 * The index lives here, the one place both halves can read it from.
 */
function OverviewSection() {
  const { questions } = ABOUT;
  const [active, setActive] = React.useState(0);

  return (
    <section className={band}>
      {/* Closer on a phone, where the first row's own padding is already the
          air under the heading. */}
      <RuledHeader title="Overview" className="mb-2 lg:mb-12" />

      {/* `items-stretch` so the panel is as tall as the list beside it and
          keeps that height whichever answer it is showing. The list itself is
          `self-center` rather than stretched: stretched to a taller panel,
          `auto-rows-fr` shared the difference out between the rows as empty
          space above and below every question. */}
      <div className="grid items-stretch gap-block lg:grid-cols-2">
        <RuledList
          className="lg:self-center"
          label="About MDS"
          accent="muted"
          titleSize="lg"
          spacing="relaxed"
          indicator
          activeIndex={active}
          onSelect={setActive}
          // On a phone the answer opens under its question instead.
          renderPanel={(i) => (
            <AnswerPanel
              id={`about-answer-${i}`}
              className="min-h-0"
              item={questions[i]!}
              index={i}
              count={questions.length}
            />
          )}
          items={questions.map((q, i) => ({
            marker: String(i + 1).padStart(2, '0'),
            title: q.question,
            controls: 'about-answer',
          }))}
        />

        <AnswerPanel
          id="about-answer"
          className="max-lg:hidden"
          item={questions[active]!}
          index={active}
          count={questions.length}
        />
      </div>
    </section>
  );
}

/** Every space's cover from the API; fewer than two while it loads or if few have one. */
function useStatementPhotos() {
  const spaces = useQuery(spaceQueries.list());
  return React.useMemo(
    () =>
      (spaces.data ?? []).flatMap((space) =>
        space.coverImage ? [{ src: space.coverImage, alt: space.name, caption: space.name }] : [],
      ),
    [spaces.data],
  );
}

const STEP = { letter: 1, word: 0.4, bodyAfter: 3, overlap: 2 } as const;

function statementTimeline(items: { title: string; body: string }[]) {
  let start = 0;
  return items.map((item) => {
    const words = item.body.split(' ');
    const row = { ...item, start, words };
    const end = start + STEP.bodyAfter + words.length * STEP.word;
    start = end - STEP.overlap;
    return row;
  });
}

function StatementSection() {
  const photos = useStatementPhotos();
  const { ref, state } = useReveal<HTMLDivElement>();
  const rows = statementTimeline([
    { title: 'Vision', body: ABOUT.vision },
    { title: 'Mission', body: ABOUT.mission },
  ]);

  // Held hidden until scrolled to, then played with the nth piece waiting n
  // steps — the three states `useReveal` hands back. In `static` (reduced
  // motion, no observer, no script) nothing here applies and the band is
  // simply drawn.
  const play = (animation: string, step: number) => ({
    className: cn(state === 'armed' && 'opacity-0', state === 'revealed' && animation),
    style:
      state === 'revealed'
        ? { animationDelay: `calc(var(--duration-stagger) * ${step})` }
        : undefined,
  });

  return (
    <section className={cn(band, 'mt-card')} aria-labelledby="statement-heading">
      <h2 id="statement-heading" className="sr-only">
        Vision and mission
      </h2>
      <StatementSwipe
        className="lg:hidden"
        label="Vision and mission"
        items={[
          { title: 'Vision', body: ABOUT.vision, image: photos[0] },
          { title: 'Mission', body: ABOUT.mission, image: photos[1] },
        ]}
      />

      <FeatureSplit
        className="max-lg:hidden"
        images={photos}
        carousel="flat"
        galleryLabel="Photos of the MDS building"
      >
        <div ref={ref} className="w-full">
          {rows.map((row, r) => {
            const rule = play('animate-rule-draw', row.start - STEP.overlap);
            return (
              <React.Fragment key={row.title}>
                {r > 0 && (
                  <span
                    aria-hidden="true"
                    style={rule.style}
                    className={cn('my-6 block h-px origin-left bg-hairline', rule.className)}
                  />
                )}

                <div>
                  <h3 className="m-0 text-5xl font-normal tracking-tight text-brand-text sm:text-6xl">
                    <span className="sr-only">{row.title}</span>
                    <span aria-hidden="true">
                      {[...row.title].map((letter, i) => {
                        const rise = play('animate-letter-rise', row.start + i * STEP.letter);
                        return (
                          // The outer span is the edge the letter comes up
                          // through: clipped, so the glyph is hidden by its own
                          // line rather than faded in.
                          //
                          // `inline-flex`, never `inline-block`: with a
                          // `--spacing-block` token in the theme, Tailwind reads
                          // `inline-block` as `inline-size: var(--spacing-block)`
                          // as well, and every letter and word came out 40px wide.
                          <span key={i} className="inline-flex overflow-hidden align-top">
                            <span style={rise.style} className={cn('inline-flex', rise.className)}>
                              {letter}
                            </span>
                          </span>
                        );
                      })}
                    </span>
                  </h3>

                  {/* Already the lightest weight loaded, so it is softened by
                      colour instead: the secondary ink rather than full ink. */}
                  <p className="m-0 mt-4 max-w-[44ch] text-body-lg font-normal text-ink-secondary">
                    {row.words.map((word, i) => {
                      const focus = play(
                        'animate-focus-in',
                        row.start + STEP.bodyAfter + i * STEP.word,
                      );
                      return (
                        <React.Fragment key={i}>
                          <span style={focus.style} className={cn('inline-flex', focus.className)}>
                            {word}
                          </span>{' '}
                        </React.Fragment>
                      );
                    })}
                  </p>
                </div>
              </React.Fragment>
            );
          })}
        </div>
      </FeatureSplit>
    </section>
  );
}

/**
 * The four core values. At rest each is a mint title with a chevron over its
 * bold line.
 *
 * On desktop, the one under the pointer tips its title on its side into a
 * narrow column on the left and opens its longer sentence beneath the bold
 * line. The tip is two copies of the title, not one turning: `writing-mode`
 * cannot be transitioned, so the upright copy's row closes to nothing while
 * the sideways copy's column opens from nothing, both on the same curve, and
 * the eye reads one word falling over. Every moving part is a grid track going
 * between `0fr` and its open size, the one way to animate to a size nobody
 * wrote down.
 *
 * Below `lg` there is no tipping: a tap on the title slides the sentence open
 * underneath and flips the chevron, and a second tap closes it. The title is a
 * real button with `aria-expanded`, so the keyboard reaches the same toggle.
 *
 * The sentence is clipped, never removed, so a screen reader reads all four
 * in full whatever the pointer is doing.
 */
function CoreValuesSection() {
  const [active, setActive] = React.useState<number | null>(null);

  // One curve for every track, so the parts move as one.
  const move = 'duration-(--duration-base) ease-standard motion-reduce:transition-none';

  return (
    <section className={band}>
      <RuledHeader title="Our values" />

      <div
        className="grid gap-block sm:grid-cols-2 lg:grid-cols-4 lg:gap-card"
        // Back to all four at rest once a mouse leaves the set, rather than
        // whenever it crosses the gap between two of them. Mouse only: a
        // finger lifting off the screen fires a leave too, and would close
        // the value it had just tapped open.
        onPointerLeave={(e) => {
          if (e.pointerType === 'mouse') setActive(null);
        }}
      >
        {ABOUT.coreValues.map((value, i) => {
          const open = active === i;

          return (
            <article
              key={value.title}
              onPointerEnter={(e) => {
                if (e.pointerType === 'mouse') setActive(i);
              }}
              className={cn(
                // Column one is the sideways title: shut at rest and on every
                // phone; 32px open on desktop — a 26px word set solid, with
                // room for its descenders.
                'grid grid-cols-[--spacing(0)_minmax(0,1fr)] items-start gap-x-0',
                'transition-[grid-template-columns,column-gap]',
                move,
                open && 'lg:grid-cols-[--spacing(8)_minmax(0,1fr)] lg:gap-x-3',
                // One column on a phone: a hairline between values, so the
                // stack reads as an accordion's rows.
                'max-sm:border-b max-sm:border-hairline max-sm:pb-6 max-sm:last:border-b-0 max-sm:last:pb-0',
              )}
            >
              {/* Desktop only, and reads bottom to top: `vertical-rl` runs it
                  downwards and the half turn stands it the other way up. Its
                  length sets the row's height even while its column is shut,
                  which keeps every value the same height open or not. Not
                  rendered below `lg`, where it would only add that height. */}
              <div className="hidden overflow-hidden lg:block">
                <span
                  aria-hidden="true"
                  className={cn(
                    'block rotate-180 font-sans text-2xl leading-none font-bold tracking-tight whitespace-nowrap',
                    'text-brand-text opacity-0 [writing-mode:vertical-rl]',
                    'transition-opacity',
                    move,
                    open && 'lg:opacity-100',
                  )}
                >
                  {value.title}
                </span>
              </div>

              {/* Pinned to column two: below `lg` the sideways title is not
                  drawn, and auto-placed this would land in its 0px column and
                  wrap one word to a line. */}
              <div className="col-start-2">
                {/* The upright title: the toggle on a phone, the resting face
                    on desktop, where it closes away as the sideways one opens. */}
                <div
                  className={cn(
                    'grid grid-rows-[1fr] transition-[grid-template-rows,opacity]',
                    move,
                    open && 'lg:grid-rows-[0fr] lg:opacity-0',
                  )}
                >
                  {/* Clipped top to bottom only: the row is what closes, and a
                      long word's chevron may run on into the gap beside it.
                      `min-h-0` because, unlike `hidden`, `clip` leaves a grid
                      item its content height as a floor and the row would
                      never close. */}
                  <div className="min-h-0 overflow-y-clip">
                    {/* The value's heading, holding its toggle — a heading a
                        screen reader can jump to, announced as expandable. */}
                    <h3 className="m-0">
                      <button
                        type="button"
                        aria-expanded={open}
                        onClick={() => setActive(open ? null : i)}
                        className={cn(
                          // Set exactly as the Overview questions (`RuledList`'s
                          // `lg` title): the same face, weight and size, a clear
                          // step under the "Our values" heading.
                          'flex items-center gap-2 pb-2 text-left font-sans text-xl font-bold tracking-tight text-brand-text lg:text-2xl',
                          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
                        )}
                      >
                        {value.title}
                        <LuChevronDown
                          aria-hidden="true"
                          className={cn(
                            'size-5 shrink-0 text-ink-muted transition-transform',
                            move,
                            open && 'rotate-180',
                          )}
                        />
                      </button>
                    </h3>
                  </div>
                </div>

                {/* Capped only in the four-across row; on a phone a tagline
                    holds its one line. */}
                <p className="m-0 font-display text-lg font-bold text-ink lg:max-w-[16ch]">
                  {value.tagline}
                </p>

                <div
                  className={cn(
                    'grid grid-rows-[0fr] transition-[grid-template-rows]',
                    move,
                    open && 'grid-rows-[1fr]',
                  )}
                >
                  <div className="overflow-hidden">
                    <p className="m-0 max-w-[48ch] pt-4 text-base leading-relaxed text-ink-secondary lg:max-w-[24ch]">
                      {value.body}
                    </p>
                  </div>
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}

/**
 * The page's opening: a two-line poster statement, the sentence that explains
 * it, and one way onwards.
 *
 * Not `PageHeader`: that is a title for an ordinary page, capped at the type
 * scale's 64px, with no call to action. This is the one headline in the
 * product set at `text-poster`, the fluid step that exists for it alone.
 *
 * The second line is mint and set in from the left, so the two read as a
 * call and its answer rather than one sentence broken in two. Indented in `em`
 * so the step scales with the type, and only from `sm` — on a phone the
 * words already fill the width and an indent would push "GLOBAL" off it.
 *
 * Both lines are one `h1`, split into blocks, so the page's heading is the
 * whole statement.
 */
function AboutHero() {
  const { hero } = ABOUT;
  const [first, second] = hero.lines;
  // Fades in on load: it is at the top of the page, so the observer finds it
  // on screen at once. The ink line, then the mint one a beat later — the
  // call, then its answer — then the sentence and the link. Drawn plainly
  // with reduced motion or no observer.
  const { ref, state } = useReveal<HTMLDivElement>();
  const rise = cn(state === 'armed' && 'opacity-0', state === 'revealed' && 'animate-reveal-up');
  const after = (step: number) =>
    state === 'revealed'
      ? { animationDelay: `calc(var(--duration-stagger) * ${step})` }
      : undefined;

  return (
    <section className={band}>
      <div ref={ref}>
        <h1 className="text-poster uppercase">
          <span style={after(0)} className={cn('block text-ink', rise)}>
            {first}
          </span>
          <span style={after(3)} className={cn('block text-brand-text sm:pl-[1.2em]', rise)}>
            {second}
          </span>
        </h1>

        <div
          style={after(6)}
          className={cn(
            'mt-block flex flex-wrap items-end justify-between gap-x-block gap-y-card',
            rise,
          )}
        >
          <p className="max-w-[48ch] text-body-lg text-ink-secondary">{hero.description}</p>

          {/* A text link rather than a button: it is a way onwards, not the
            page's action. The mint rule under it is its edge, and the arrow
            nudges on hover so the response is not carried by colour alone. */}
          <Link
            to={hero.cta.to}
            className={cn(
              'group inline-flex items-center gap-3 border-b-2 border-brand-edge pb-2',
              'text-sm font-semibold text-ink',
              'focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-focus',
            )}
          >
            {hero.cta.label}
            <LuArrowRight
              aria-hidden="true"
              className={cn(
                'size-4 transition-transform duration-(--duration-fast) ease-standard',
                'group-hover:translate-x-1 motion-reduce:transition-none',
              )}
            />
          </Link>
        </div>
      </div>
    </section>
  );
}

/**
 * Who MDS is for: three audience cards — the number, the audience, who that
 * is, and what they are looking for.
 *
 * White cards on the page: a small number, the audience in mint, who that is
 * in bold, a hairline, then what they are looking for as one sentence. The
 * number is kept small on purpose — it only orders the three, and set large it
 * outweighed the name it sits over.
 *
 * As the row scrolls into view the cards rise in one after another, two
 * stagger steps apart, like the page's other card rows. Drawn plainly with
 * reduced motion or no observer.
 *
 * On a phone they become a sideways row that snaps card by card, each card
 * most of the screen wide so the next one peeks in and says the row scrolls.
 */
function AudiencesSection() {
  const { ref, state } = useReveal<HTMLDivElement>();

  return (
    <section className={band}>
      <RuledHeader title="Who MDS is for" />

      <div
        ref={ref}
        className={cn(
          // No bottom padding: it pushed the next section 8px further away than
          // every other gap on the page.
          '-mx-gutter flex snap-x snap-mandatory gap-card overflow-x-auto px-gutter',
          'md:mx-0 md:grid md:grid-cols-3 md:overflow-visible md:px-0',
        )}
      >
        {ABOUT.audiences.map((audience, i) => (
          <article
            key={audience.name}
            style={
              state === 'revealed'
                ? { animationDelay: `calc(var(--duration-stagger) * ${i * 2})` }
                : undefined
            }
            className={cn(
              'flex w-[82%] shrink-0 snap-start flex-col rounded-card border border-hairline bg-surface p-card',
              // From `md`, where the three sit side by side, each card spans
              // the row's four tracks (title, who, rule, text) as a subgrid:
              // a title that wraps to two lines makes that track two lines tall
              // in all three, so every card's text starts on one line.
              'lg:p-block md:row-span-4 md:grid md:w-auto md:grid-rows-subgrid md:gap-y-0',
              state === 'armed' && 'opacity-0',
              state === 'revealed' && 'animate-reveal-up',
            )}
          >
            {/* `text-xl`: at 2xl the longest name, "Global Employers &
                Partners", wrapped to two lines in its third of the row. */}
            <h3 className="font-display text-xl font-semibold text-brand-text">{audience.name}</h3>
            <p className="mt-3 text-base font-semibold text-ink">{audience.who}</p>

            <hr className="my-card border-hairline" />

            {/* One sentence: "Looking for credible local opportunities…". The
                copy starts with a capital, so its first letter is lowered
                here rather than the data rewritten around this one reading. */}
            <p className="text-base leading-relaxed text-ink-secondary">
              Looking for {audience.lookingFor.charAt(0).toLowerCase()}
              {audience.lookingFor.slice(1)}
            </p>
          </article>
        ))}
      </div>
    </section>
  );
}

/**
 * "Inside the square": the spaces as a rail of cards, each a link to its own
 * page. Read from the spaces API and drawn with `SpaceCard`, the same list and
 * the same card as the home rail, so a space added or renamed in the admin app
 * shows up here too.
 *
 * Unnumbered: the numbered chapters are about MDS; this is the way into the
 * building. The tagline is desktop only, as in the design.
 */
function SquareSection() {
  const { square } = ABOUT;
  const spaces = useQuery(spaceQueries.list());

  return (
    <section className={band}>
      {/* Tight to the tagline from `md`; on a phone the tagline is not drawn,
          so the header keeps its usual air to the cards. */}
      <RuledHeader title={square.title} className="md:mb-card" />
      <p className="mb-block hidden font-mono text-xs tracking-widest text-ink-muted uppercase md:block">
        {square.tagline}
      </p>

      {spaces.isPending ? (
        <LoadingBlock rows={1} />
      ) : spaces.isError ? (
        <ErrorBlock
          title="We couldn't load the spaces"
          error={spaces.error}
          onRetry={() => void spaces.refetch()}
        />
      ) : (
        <CardRail label={square.title}>
          {spaces.data.map((space) => (
            <SpaceCard key={space.id} space={space} />
          ))}
        </CardRail>
      )}
    </section>
  );
}

/**
 * The About page: why MDS exists and what it does, where it is going and what
 * it values, who it is for, then what is inside the building.
 * Each idea is said once; see `ABOUT` for what was cut.
 */
export default function AboutPage() {
  return (
    <>
      <AboutHero />
      <PhotoStrip />

      <ProblemSection />
      <OverviewSection />
      <StatementSection />
      <CoreValuesSection />
      <AudiencesSection />
      <SquareSection />
    </>
  );
}
